/**
 * /blog/introducing-starter-and-growth · Cyannect launch post (2026-02).
 *
 * Marketing copy owner: Cyannect team. All product facts (pricing, add-on
 * mechanics, per-storefront province allowance, annual discount, dual
 * redemption, cy_ref attribution) are pulled from the live codebase so
 * they never drift from the app.
 */
import { Link } from "react-router-dom";
import { Calendar, ArrowLeft, MapPin, Store, TrendingUp, Zap, Percent, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { usePricing } from "@/lib/pricing";

const PUBLISHED = "2026-02-01";

export default function LaunchPost() {
  const pricing = usePricing();
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white">
      <Seo
        title="Introducing Cyannect Starter and Growth"
        description="Cyannect is now open to Canadian merchants. Starter and Growth plans include per-storefront province coverage plus a flat 15% discount on annual add-ons."
        path="/blog/introducing-starter-and-growth"
      />
      <article className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post-launch">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-6"
          data-testid="blog-post-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>

        {pricing?.final_active && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="blog-post-pricing-outdated">
            <strong>Pricing in this post is no longer current.</strong> The plan prices and fees below were current
            when this post was published. <Link to="/pricing" className="underline font-semibold" data-testid="blog-post-current-pricing-link">See current pricing</Link>.
          </div>
        )}

        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 rounded-full bg-cream-50 border border-cream-200 px-2 py-0.5 text-burgundy-800 font-semibold uppercase tracking-widest">
            Product launch
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(PUBLISHED).toLocaleDateString("en-CA", {
              year: "numeric", month: "long", day: "numeric",
            })}
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-medium text-slate-900 mt-4">
          Introducing Cyannect Starter and Growth
        </h1>
        <p className="text-lg text-slate-700 mt-4">
          Performance commerce for Canadian merchants — with per-storefront
          province coverage and a flat 15% discount on annual add-ons.
        </p>

        <div className="prose prose-slate max-w-none mt-10 space-y-6 text-slate-700 leading-relaxed">
          <p>
            Today we're opening Cyannect's merchant portal to brands across
            Canada. If you sell online (or run bricks-and-mortar shops that
            accept coupons), Cyannect gives you a performance-marketing
            channel that only bills for verified sales — and puts your best
            offers in front of Canadian shoppers looking for them.
          </p>

          <h2 id="two-plans">Two plans, no surprises</h2>
          <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
            <div className="pf-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-slate-500" />
                <h3 className="font-display text-xl font-semibold text-slate-900">Starter</h3>
              </div>
              <div className="text-3xl font-bold text-slate-900">$39.99<span className="text-sm font-medium text-slate-500">/mo</span></div>
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                <li>1 merchant storefront</li>
                <li>1 Canadian province included</li>
                <li>Manual coupon creation + product-level offers</li>
                <li>4.25% or CAD $1.99 per verified sale (whichever is higher)</li>
              </ul>
            </div>
            <div className="pf-card p-5 border-2 border-burgundy-300 bg-cream-50/40">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-burgundy-600" />
                <h3 className="font-display text-xl font-semibold text-slate-900">Growth</h3>
                <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-burgundy-600 text-white">Popular</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">$79.99<span className="text-sm font-medium text-slate-500">/mo</span></div>
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                <li>3 storefronts included</li>
                <li>Up to 3 Canadian provinces per storefront</li>
                <li>AI-assisted coupon creation + advanced reporting</li>
                <li>2.85% or CAD $1.49 per verified sale (whichever is higher)</li>
              </ul>
            </div>
          </div>

          <h2 id="province-coverage">Per-storefront province coverage</h2>
          <p>
            Cyannect is province-aware by design. Instead of a single
            account-wide province list, coverage now lives on each
            <em> storefront</em> — so a Growth merchant with three storefronts can
            operate in three different provinces per storefront, all included.
            Need to expand? Add a province for CAD $11.99/month, or add another
            storefront for CAD $19.99/month.
          </p>
          <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
            <div className="pf-card p-4 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-slate-900">Add a province</div>
                <div className="text-slate-600">CAD $11.99/month per additional province, attached to a specific storefront.</div>
              </div>
            </div>
            <div className="pf-card p-4 flex items-start gap-3">
              <Store className="w-5 h-5 text-burgundy-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-slate-900">Add a storefront</div>
                <div className="text-slate-600">CAD $19.99/month per additional distinct brand or business unit — bundles up to 3 provinces on either plan.</div>
              </div>
            </div>
          </div>

          <h2 id="annual-discount">Save 15% when you pay annually</h2>
          <p>
            Every Cyannect add-on now ships with a flat <strong>15% annual
            discount</strong>. Instead of paying 12 &times; monthly, an annual
            add-on is 12 &times; monthly &times; 0.85 — and it's billed on the
            same anniversary as your base plan, so you never juggle multiple
            renewal dates.
          </p>
          <div className="not-prose pf-card p-5 my-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 mb-2">
              <Percent className="w-4 h-4" /> Example
            </div>
            <p className="text-sm text-slate-700">
              A Growth merchant with 4 provinces on one storefront pays{" "}
              <strong>$11.99/month</strong> for the extra province, or{" "}
              <strong>$122.30/year</strong> if billed annually — that's{" "}
              <strong>15% off</strong> vs. paying month-to-month.
            </p>
          </div>

          <h2 id="dual-redemption">Two redemption flows, one product</h2>
          <p>
            Cyannect supports both discount styles Canadian merchants actually
            use:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>
              <strong>Automatic Discount</strong> — shoppers open your site via
              a Cyannect link carrying a <code>cy_ref</code> attribution token;
              your storefront applies the discount at checkout without a code.
            </li>
            <li>
              <strong>Coupon Code</strong> — Cyannect issues a unique code, the
              shopper copies it, and applies it at your normal checkout.
            </li>
          </ul>

          <h2 id="verified-sale">You only pay for verified sales</h2>
          <div className="not-prose pf-card p-5 flex items-start gap-3 my-6">
            <ShieldCheck className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              The performance fee only applies to sales that flow through your
              conversions integration and clear our finality window. Refunds
              and chargebacks reverse cleanly. Cyannect never touches your
              customer's transaction — your commerce and payment stack stay
              exactly where they are.
            </div>
          </div>

          <h2 id="whats-next">What ships next</h2>
          <p>We're rolling out the following over the next few weeks:</p>
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>Push notifications to favourite-brand followers within seconds of a new approved offer.</li>
            <li>A 60-second onboarding video inside every new merchant's storefront tour.</li>
            <li>WooCommerce plugin (Shopify already live).</li>
          </ul>

          <h2 id="cta">Ready to launch?</h2>
          <p>
            Head to{" "}
            <Link to="/pricing" className="underline text-burgundy-700">/pricing</Link>{" "}
            to compare Starter and Growth, or start your merchant sign-up
            directly at{" "}
            <Link to="/register?role=merchant" className="underline text-burgundy-700">
              /register?role=merchant
            </Link>. Questions? Reach the founding team at{" "}
            <a href="mailto:hello@cyannect.com" className="underline text-burgundy-700">
              hello@cyannect.com
            </a>.
          </p>
        </div>

        <div className="mt-12 pf-card p-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div>
            <div className="font-display text-xl font-semibold text-slate-900">
              List your brand on Cyannect
            </div>
            <p className="text-slate-600 text-sm mt-1">
              Sign-up takes about a minute. Card required only when you activate a plan.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link
              to="/pricing"
              className="px-4 py-2 rounded-full text-sm font-semibold border border-slate-300 hover:border-slate-500"
              data-testid="blog-cta-pricing"
            >
              See pricing
            </Link>
            <Link
              to="/register?role=merchant"
              className="px-4 py-2 rounded-full text-sm font-semibold bg-burgundy-600 text-white hover:bg-burgundy-700"
              data-testid="blog-cta-signup"
            >
              Get started
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
